import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { STORY_STATUS_LABEL, STORY_STATUS_ORDER, type KennzahlenDto, type SnapshotDto } from '../../../lib/api/abnahme';
import { ACHSE, ChartKarte, FLAECHE, Legende, RASTER, STAPEL_REIHENFOLGE, STATUS_FARBE, VizTooltip, statusLegende, tagKurz, tagLang } from './viz';

type Punkt = { tag: string } & Record<string, number | string>;

/** Verlauf aus den Tagesständen, heute live angehängt. */
function burnUpDaten(snapshots: SnapshotDto[], heute?: { tag: string; stories: KennzahlenDto['stories'] }): Punkt[] {
  const punkte: Punkt[] = snapshots
    .flatMap((s) => (s.stories ? [{ tag: s.tag, ...s.stories.status }] : []));
  if (heute && !punkte.some((p) => p.tag === heute.tag)) punkte.push({ tag: heute.tag, ...heute.stories.status });
  return punkte;
}

/**
 * Burn-up der User Stories: gestapelt nach Status, Abgenommenes unten.
 * Rekonstruierte Tage (aus der Historie nachgerechnet) sind mit einer Linie abgesetzt.
 */
export default function BurnUp({
  snapshots,
  heute,
  titel = 'Fortschritt der User Stories',
}: {
  snapshots: SnapshotDto[];
  heute?: { tag: string; stories: KennzahlenDto['stories'] };
  titel?: string;
}) {
  const daten = burnUpDaten(snapshots, heute);
  const letzterRekonstruiert = [...snapshots].reverse().find((s) => s.rekonstruiert)?.tag;
  return (
    <ChartKarte
      titel={titel}
      beschreibung={
        letzterRekonstruiert
          ? `Bis ${tagLang(letzterRekonstruiert)} aus der Historie rekonstruiert, danach täglich festgehalten.`
          : 'Täglich um 23:50 festgehalten.'
      }
      legende={<Legende eintraege={statusLegende()} />}
      tabelle={{
        spalten: ['Tag', ...STORY_STATUS_ORDER.map((s) => STORY_STATUS_LABEL[s])],
        zeilen: [...daten].reverse().map((p) => [tagLang(p.tag), ...STORY_STATUS_ORDER.map((s) => Number(p[s] ?? 0))]),
      }}
    >
      {daten.length < 2 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Der Verlauf entsteht, sobald mindestens zwei Tage festgehalten sind.
        </p>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daten} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid {...RASTER} />
              <XAxis dataKey="tag" tickFormatter={tagKurz} minTickGap={24} {...ACHSE} />
              <YAxis allowDecimals={false} {...ACHSE} axisLine={false} />
              <Tooltip content={<VizTooltip labelFormat={(l) => tagLang(String(l))} />} />
              {letzterRekonstruiert && (
                <ReferenceLine x={letzterRekonstruiert} stroke="var(--viz-text)" strokeDasharray="3 3" />
              )}
              {STAPEL_REIHENFOLGE.map((status) => (
                <Area
                  key={status}
                  type="stepAfter"
                  dataKey={status}
                  name={STORY_STATUS_LABEL[status]}
                  stackId="s"
                  fill={STATUS_FARBE[status]}
                  fillOpacity={0.9}
                  stroke={FLAECHE}
                  strokeWidth={1}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartKarte>
  );
}
